import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { createPortal } from 'react-dom';
import Select, { components } from 'react-select';
import type { OptionProps, MultiValue, MultiValueRemoveProps, StylesConfig } from 'react-select';
import CorgeeStorageHelper from './CorgeeStorageHelper';
import { getVisibleLayers, getLayersName } from './ArrayUtil';
import * as apiHelper from './Utils';
import { nanoid } from 'nanoid';
import RenderInBody from './RenderInBody';
import ClickOutSide from './ClickOutSide';
import './ConversationOwner.css';
import $ from 'jquery';
import { socketService } from '@/services/socketService';
import { conversationBus } from '@/lib/conversationBus';

// Hides and disables the remove button; keeps its height so the chip does not shrink, collapses its width
const LockedMultiValueRemove = (props: MultiValueRemoveProps<Layer, true>) => (
  <components.MultiValueRemove
    {...props}
    innerProps={{
      ...props.innerProps,
      onClick: undefined,
      onMouseDown: undefined,
      onTouchEnd: undefined,
      'aria-hidden': true,
      style: {
        visibility: 'hidden',
        pointerEvents: 'none',
        width: 0,
        minWidth: 0,
        paddingLeft: 0,
        paddingRight: 0,
        overflow: 'hidden',
      },
    }}
  />
);

// Interfaces
interface Layer {
  LayerId: number;
  Name?: string;
  LayerName?: string;
  Color?: string;
  PriorityInConfigValue?: number;
  IsAvailable?: boolean;
  layerInConversation?: boolean;
  disabled?: boolean;
  ProjectConversationId?: number;
  configValueId?: number;
  isConfigLayer?: boolean;
}

interface Conversation {
  ConversationId?: number;
  ConversationLayers: Layer[];
  ConfigValueId: number;
  selectedConfigValueId?: number;
  ProjectId?: number;
  ProjectID?: number;
  ProjectConversationId?: number;
  OwnedById?: number;
  IsEnable?: boolean;
  isAddLayerToCard?: boolean;
}

interface ApiConfig {
  url: string;
  token: string;
}

interface LayerFunctions {
  updateLayerToCard?: (layerId: number) => void;
  onUpdateLayerToCard?: (layerId: number) => void;
}

interface CardConversationLayersProps {
  apiConfig: ApiConfig;
  itemType?: number;
  conversation: Conversation;
  disableAutoSave?: boolean;
  isNew?: boolean;
  isNewRepost?: boolean;
  /** When true (e.g. duplicate), keep conversation.ConversationLayers as the initial selection
   *  instead of picking the personal default from GetLayersByConfigValueId. */
  preserveInitialLayers?: boolean;
  /** When true, the last layer cannot be removed even if the card is new or not autosaved
   *  (e.g. inline card creation). Existing autosaved cards always keep one layer. */
  keepOneLayer?: boolean;
  layersSelectedIds?: number[];
  isConversationDetailsView?: boolean;
  isInlineWidget?: boolean;
  customStyles?: any;
  restrictToConversationLayers?: boolean;
  /** When true, config layers (GetLayersByConfigValueId) are shown as plain selectable options
   *  without the "Add?" prompt. Used in EditCard where layers are saved on submit, not immediately. */
  selectableConfigLayers?: boolean;
  /** When true, hides the "This card does not have X layer" prompt. Use in header/custom-fields context
   *  where the add-layer message is only appropriate in the reply box. */
  hideAddLayerPrompt?: boolean;
  /** Closes the dropdown right after picking an option. */
  closeMenuOnSelect?: boolean;
  onChangedLayers?: (layers: Layer[] | Conversation, fromInfoWindow?: boolean) => void;
  callbackDisableEditor?: (callback: (isKeydown?: boolean) => void) => void;
  onSetFunctionsCardLayers?: (functions: LayerFunctions) => void;
  addLayerToCard?: (
    e: React.MouseEvent,
    layer: Layer,
    functions?: LayerFunctions,
    conversationId?: number
  ) => void;
  addLayerToProject?: (e: React.MouseEvent, layer: Layer, functions?: LayerFunctions) => void;
  section?: string;
  configData?: any;
}

export interface CardConversationLayersRef {
  onSelectLayer: (layers: Layer[]) => void;
  onUpdateOriginalSelectedIds: (layers: Layer[]) => void;
}

let waitTimeNotification = 0;

const CardConversationLayers = forwardRef<CardConversationLayersRef, CardConversationLayersProps>(
  (
    {
      apiConfig,
      itemType: _itemType,
      conversation,
      disableAutoSave = false,
      isNew = false,
      isNewRepost = false,
      preserveInitialLayers = false,
      keepOneLayer: keepOneLayerProp = false,
      layersSelectedIds = [],
      isConversationDetailsView = false,
      onChangedLayers,
      callbackDisableEditor,
      onSetFunctionsCardLayers,
      addLayerToCard,
      addLayerToProject,
      isInlineWidget = false,
      customStyles,
      restrictToConversationLayers = false,
      selectableConfigLayers = false,
      hideAddLayerPrompt = false,
      closeMenuOnSelect = false,
      configData,
    },
    ref,
  ) =>
  {
    const containerRef = useRef<HTMLDivElement>(null);
    const selectRef = useRef<any>(null);

    // A card must keep at least one layer (Legacy parity)
    const keepOneLayer = keepOneLayerProp || (!isNew && !disableAutoSave);

    const elementId = useMemo(() => nanoid(), []);

    useImperativeHandle(ref, () => ({
      onSelectLayer: (layers: Layer[]) =>
      {
        const filtered = layers.filter(x => x.LayerId !== 0);
        const selectedValues = filtered.map(item => item.LayerId);
        const selectedNamesNew = getLayersName(filtered);
        setLayersSelected(filtered);
        setSelectedIds(selectedValues);
        setSelectedNames(selectedNamesNew);
      },
      onUpdateOriginalSelectedIds: (layers: Layer[]) =>
      {
        const filtered = layers.filter(x => x.LayerId !== 0);
        const selectedValues = filtered.map(item => item.LayerId);
        setOriginalSelectedIds(selectedValues);
        setOriginalSelectedLayers(filtered);
      },
    }));

    const isRecycleBin = window.location.href.indexOf('RecycleBin') > -1;

    const getCanEdit = useCallback((): boolean =>
    {
      if (!conversation) return false;

      let canEditFixed = false;
      if (!conversation.IsEnable && isRecycleBin)
      {
        canEditFixed = CorgeeStorageHelper.canEdit() && !!conversation.IsEnable;
      }
      else
      {
        canEditFixed =
          CorgeeStorageHelper.canEdit() ||
          conversation.OwnedById === CorgeeStorageHelper.personId() ||
          CorgeeStorageHelper.isAdmin();
      }

      const personLayers = CorgeeStorageHelper.getAllLayers();
      if (personLayers.length === 1 && !isNew)
      {
        canEditFixed = false;
      }

      return canEditFixed;
    }, [conversation, isNew, isRecycleBin]);

    const [canEdit] = useState<boolean>(getCanEdit());
    const [layersSelected, setLayersSelected] = useState<Layer[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [selectedNames, setSelectedNames] = useState<string>('Not Set');
    const [configValueId, setConfigValueId] = useState<number>(
      conversation.selectedConfigValueId ?? conversation.ConfigValueId,
    );
    const [projectId, setProjectId] = useState<number | undefined>(
      conversation.ProjectID ?? conversation.ProjectId,
    );
    const [dataLayers, setDataLayers] = useState<Layer[]>([]);
    const [originalSelectedIds, setOriginalSelectedIds] = useState<number[]>([]);
    const [_originalSelectedLayers, setOriginalSelectedLayers] = useState<Layer[]>([]);
    // Prevents re-picking the default layer on subsequent fetchLayers calls (e.g. from handleEnableEditor).
    const hasPickedDefaultLayer = useRef(false);
    const [isFromInfoWindow, setIsFromInfoWindow] = useState<boolean>(false);
    const [isFromTabDetails, setIsFromTabDetails] = useState<boolean>(false);
    const [editorEnabled, setEditorEnabled] = useState<boolean>(false);
    const [posTop, setPosTop] = useState(0);
    const [posLeft, setPosLeft] = useState(0);
    const [CSSPosition, setCSSPosition] = useState<'fixed' | 'absolute'>('fixed');
    const [projectConfirmLayer, setProjectConfirmLayer] = useState<Layer | null>(null);
    const [projectConfirmEvent, setProjectConfirmEvent] = useState<React.MouseEvent | null>(null);

    const mergeLayers = useCallback(
      (
        currentLayers: Layer[],
        userLayers: Layer[],
        projectconversationid?: number,
        configValueIdParam?: number,
      ): Layer[] =>
      {
        const currentLayersCopy = [...currentLayers];
        let result = currentLayersCopy.concat(userLayers);

        // Remove duplicates
        result = result.filter(
          (obj, index, self) => self.findIndex(o => o.LayerId === obj.LayerId) === index,
        );

        // All layers from GetLayersByConfigValueId are always selectable (isConfigLayer = true).
        // Only extra layers from GetLayersForCardBy that are not config layers need the "Add?" action.
        currentLayersCopy.forEach(currentLayer =>
        {
          const found = result.find(r => r.LayerId === currentLayer.LayerId);
          if (found)
          {
            found.IsAvailable = true;
            found.layerInConversation = false;
            found.isConfigLayer = true;
          }
        });

        // Mark extra layers (Layers For Card).
        // These are available-to-add layers that are NOT part of the project config.
        // isConfigLayer stays false — that flag drives the "Not available on this card" message.
        userLayers.forEach(userLayer =>
        {
          const found = result.find(r => r.LayerId === userLayer.LayerId);
          if (found && !found.isConfigLayer)
          {
            found.IsAvailable = true;
            if (found.layerInConversation === undefined)
            {
              found.layerInConversation = false;
            }
          }
        });

        // Mark layers in conversation
        conversation.ConversationLayers.forEach(convLayer =>
        {
          const found = result.find(r => r.LayerId === convLayer.LayerId);
          if (found)
          {
            found.layerInConversation = true;
          }
        });

        // Set properties for all layers
        result.forEach(layer =>
        {
          layer.ProjectConversationId = projectconversationid;
          layer.configValueId = configValueIdParam;

          if (restrictToConversationLayers)
          {
            // In reply mode: only layers already on the conversation are selectable
            layer.disabled = !layer.layerInConversation;
          }
          else if (hideAddLayerPrompt && !layer.isConfigLayer)
          {
            // In hideAddLayerPrompt mode (HeaderDetails), only GetLayersByConfigValueId layers
            // are available — personal/default layers not in that set must be disabled
            layer.disabled = true;
          }
          else if (
            !layer.IsAvailable ||
            (layer.IsAvailable && !layer.layerInConversation && !layer.isConfigLayer)
          )
          {
            layer.disabled = true;
          }
        });

        return result;
      },
      [conversation.ConversationLayers, restrictToConversationLayers, hideAddLayerPrompt],
    );

    const fetchLayers = useCallback(async (): Promise<Layer[] | undefined> =>
    {
      const activeConfigValueId =
        conversation?.ConfigValueId === 9
          ? 9
          : configValueId || conversation?.selectedConfigValueId || conversation?.ConfigValueId;

      // Prevent bad requests if required properties are not yet available
      if (!activeConfigValueId && !conversation?.ProjectId && !conversation?.ProjectID)
      {
        return undefined;
      }

      let params = `?configValueId=${activeConfigValueId || ''}`;

      const activeProjectId = conversation?.ProjectId || conversation?.ProjectID;
      if (activeProjectId)
      {
        params += `&projectId=${activeProjectId}`;
      }
      if (conversation?.ConversationId)
      {
        params += `&conversationId=${conversation.ConversationId}`;
      }

      try
      {
        const dataResponse = await apiHelper.get(
          apiConfig,
          `api/layer/GetLayersByConfigValueId${params}`,
        );

        if (activeConfigValueId !== 9)
        {
          let params2 = `?configValueId=${activeConfigValueId || ''}`;
          if (activeProjectId)
          {
            params2 += `&projectId=${activeProjectId}`;
          }

          try
          {
            const layersToAdd = await apiHelper.get(
              apiConfig,
              `api/layer/GetLayersForCardBy${params2}`,
            );

            const mergedLayers = mergeLayers(
              dataResponse,
              layersToAdd,
              conversation.ProjectConversationId,
              conversation.ConfigValueId,
            );

            const finalDataLayers = getVisibleLayers(mergedLayers);
            setDataLayers(finalDataLayers);
            return finalDataLayers;
          }
          catch (error)
          {
            console.warn('Api Error', error);
          }
        }
        else
        {
          const finalDataLayers = getVisibleLayers(dataResponse);
          setDataLayers(finalDataLayers);
          return finalDataLayers;
        }
      }
      catch (error)
      {
        console.warn('Api Error', error);
      }
      return undefined;
    }, [apiConfig, configValueId, conversation, mergeLayers]);

    // Readjust position
    const readjustPosition = useCallback(() =>
    {
      const fieldContainer = document.getElementById('field-container-' + elementId);
      if (fieldContainer)
      {
        const fce = fieldContainer.getBoundingClientRect();
        let newPosLeft = fce.left;
        let newPosTop = fce.top;
        let newCSSPosition: 'fixed' | 'absolute' = 'fixed';

        // Adjust for container context if needed
        const conversationDetailsViewBody = fieldContainer.closest('.conversationDetailsViewBody');
        if (conversationDetailsViewBody)
        {
          const parentRect = conversationDetailsViewBody.getBoundingClientRect();
          newPosTop = fce.top - parentRect.top;
          newPosLeft = fce.left - parentRect.left;
          newCSSPosition = 'absolute';
        }

        setPosTop(newPosTop);
        setPosLeft(newPosLeft);
        setCSSPosition(newCSSPosition);
      }
    }, [elementId]);

    const handleEnableEditor = useCallback(
      (_e: React.MouseEvent) =>
      {
        if (canEdit && !editorEnabled)
        {
          const openEditor = () =>
          {
            setEditorEnabled(true);
            setTimeout(() =>
            {
              readjustPosition();
              if (selectRef.current)
              {
                selectRef.current.focus();
              }
            }, 0);
          };

          // Layers already loaded from mount fetch — open editor directly without re-fetching
          if (dataLayers.length > 0)
          {
            openEditor();
          }
          else
          {
            fetchLayers().then(openEditor);
          }
        }
      },
      [canEdit, editorEnabled, fetchLayers, readjustPosition, dataLayers],
    );

    const onCloseSelect = useCallback(() =>
    {
      setEditorEnabled(false);
    }, []);

    const onSaveByBlur = useCallback(() =>
    {
      // Clean up tooltips
      const tooltips = document.querySelectorAll('.help-tooltip-item');
      tooltips.forEach(el => el.remove());

      if (containerRef.current)
      {
        const $element = $(containerRef.current);
        $element.closest('.layer-details-container').css('overflow', '');
        $('body').css('overflow', '');
      }

      if (selectedIds.sort().toString() !== originalSelectedIds.sort().toString())
      {
        if (isNew)
        {
          const itemCopy = { ...conversation };
          const layersSelectedCopy = [...layersSelected];
          itemCopy.ConversationLayers = layersSelectedCopy;

          onChangedLayers?.(itemCopy, isFromInfoWindow);
          setOriginalSelectedIds(selectedIds);
          setOriginalSelectedLayers(layersSelectedCopy);
        }
        else if (!disableAutoSave && selectedIds.some(id => id !== 0))
        {
          // For existing items, update is usually handled via callback or context in parent
          const itemCopy = { ...conversation, ConversationLayers: layersSelected };
          onChangedLayers?.(itemCopy, isFromInfoWindow);
          setOriginalSelectedIds(selectedIds);
          setOriginalSelectedLayers(layersSelected);
          // Not calling API directly here as CardConversationLayers usually delegates to parent via onChangedLayers
          // But if we need to call API directly:
          // updateLayerToCard was doing update logic
        }
      }
      onCloseSelect();
    }, [
      selectedIds,
      originalSelectedIds,
      isNew,
      conversation,
      layersSelected,
      isFromInfoWindow,
      onChangedLayers,
      disableAutoSave,
      onCloseSelect,
    ]);

    // Deferred blur handler: when the user clicks a react-select option, the browser fires
    // blur on the input BEFORE the click (mousedown → blur → click). The setTimeout(0) lets
    // the click finish first, then checks whether focus returned to the container. If it did,
    // the blur was spurious (option click) and we leave the select open. Only close if focus
    // actually left the select entirely (e.g. Tab key navigation).
    const handleSelectBlur = useCallback(() =>
    {
      setTimeout(() =>
      {
        const container = document.getElementById(`select-render-layer-${elementId}`);
        if (container?.contains(document.activeElement)) return;
        onSaveByBlur();
      }, 0);
    }, [elementId, onSaveByBlur]);

    const resolveDefaultLayers = useCallback(
      (layers: Layer[]): Layer[] =>
      {
        if (!isNew || layers.length === 0) return layers;
        const storedLayerId = parseInt(localStorage.getItem('C2_layerid') ?? '0', 10);
        const storedLayer =
          storedLayerId > 0 ? layers.find(l => l.LayerId === storedLayerId) : undefined;
        return [storedLayer ?? layers[0]!];
      },
      [isNew],
    );

    const setInitialLayer = useCallback(() =>
    {
      const layers = conversation.ConversationLayers?.filter(x => x.LayerId !== 0) ?? [];
      const layersSelectedNew = resolveDefaultLayers(layers);
      const selectedValues = layersSelectedNew.map(x => x.LayerId);
      const selectedNamesNew = getLayersName(layersSelectedNew);

      setLayersSelected(layersSelectedNew);
      setSelectedIds(selectedValues);
      setSelectedNames(selectedNamesNew);
      setOriginalSelectedIds(selectedValues);
      setOriginalSelectedLayers(layersSelectedNew);
    }, [conversation.ConversationLayers, resolveDefaultLayers]);

    const setLayersRepost = useCallback(
      (layersSelectedIdsParam: number[]) =>
      {
        const selectedValues: number[] = [];
        const layersSelectedNew: Layer[] = [];

        layersSelectedIdsParam.forEach(selectedId =>
        {
          // A reposted reply can carry a layer that is not on the card
          const found =
            conversation.ConversationLayers.find(layer => layer.LayerId === selectedId) ??
            dataLayers.find(layer => layer.LayerId === selectedId);
          if (found)
          {
            selectedValues.push(found.LayerId);
            layersSelectedNew.push(found);
          }
        });

        const selectedNamesNew = getLayersName(layersSelectedNew);

        setLayersSelected(layersSelectedNew);
        setSelectedIds(selectedValues);
        setSelectedNames(selectedNamesNew);
        setOriginalSelectedIds(selectedValues);
        setOriginalSelectedLayers(layersSelectedNew);
      },
      [conversation.ConversationLayers, dataLayers],
    );

    const setFunctions = useCallback(() =>
    {
      const functions: LayerFunctions = {
        updateLayerToCard,
      };

      onSetFunctionsCardLayers?.(functions);
    }, [onSetFunctionsCardLayers]);

    const onDisableEditor = useCallback(
      (isKeydown?: boolean) =>
      {
        if (selectedIds.sort().toString() !== originalSelectedIds.sort().toString() && !isKeydown)
        {
          if (isNew)
          {
            const itemCopy = { ...conversation };
            const layersSelectedCopy = [...layersSelected];
            itemCopy.ConversationLayers = layersSelectedCopy;

            onChangedLayers?.(itemCopy, isFromInfoWindow);
            setOriginalSelectedIds(selectedIds);
            setOriginalSelectedLayers(layersSelectedCopy);
          }
        }
      },
      [
        selectedIds,
        originalSelectedIds,
        isNew,
        conversation,
        layersSelected,
        isFromInfoWindow,
        onChangedLayers,
      ],
    );

    const onSelectLayer = useCallback(
      (newValue: MultiValue<Layer>) =>
      {
        if (keepOneLayer && newValue.length === 0)
        {
          return;
        }

        const itemsSelected = [...newValue] as Layer[];
        const selectedValues = itemsSelected.map(item => item.LayerId);
        const selectedNamesNew = getLayersName(itemsSelected);

        setLayersSelected(itemsSelected);
        setSelectedIds(selectedValues);
        setSelectedNames(selectedNamesNew);

        // Note: We don't save immediately on select in multi-select usually,
        // but here we might want to update state. Saving happens on blur.

        // However, original code updated conversation immediately
        const updatedConversation = { ...conversation, ConversationLayers: itemsSelected };
        // onChangedLayers?.(updatedConversation); // Moved to blur/save to avoid premature updates during editing?
        // Actually original updated immediately. Let's keep updating parent for immediate feedback if needed but save usually on blur
        onChangedLayers?.(updatedConversation);

        if (!isNew && !disableAutoSave)
        {
          apiHelper
            .put(apiConfig, 'api/conversation/UpdateLayers', { ...updatedConversation, SocketConnectionId: socketService.connectionId })            .catch(err => console.error('Failed to update layers', err));

          if (updatedConversation.ConversationId)
          {
            conversationBus.emit('layers-updated', {
              conversationId: updatedConversation.ConversationId,
              layers: itemsSelected as any,
            });
          }
        }
      },
      [conversation, onChangedLayers, isNew, disableAutoSave, keepOneLayer, apiConfig],
    );

    const updateLayerToCard = useCallback(
      (layerId: number) =>
      {
        // addLayerToCardHandler already optimistically set layerInConversation = true in dataLayers.
        // Calling fetchLayers() here would overwrite that with stale conversation.ConversationLayers
        // (parent hasn't re-rendered yet), making the layer disabled again.
        // Look in dataLayers first; fall back to conversation.ConversationLayers.
        const itemsSelected = [...layersSelected];
        const valueToInsert =
          dataLayers.find(x => x.LayerId === layerId) ??
          conversation.ConversationLayers.find(x => x.LayerId === layerId);

        if (valueToInsert)
        {
          itemsSelected.push(valueToInsert);
        }

        const selectedValues = itemsSelected.map(item => item.LayerId);
        const selectedNamesNew = getLayersName(itemsSelected);

        setLayersSelected(itemsSelected);
        setSelectedIds(selectedValues);
        setSelectedNames(selectedNamesNew);

        const updatedConversation = { ...conversation, ConversationLayers: itemsSelected };
        onChangedLayers?.(updatedConversation, true);
      },
      [conversation, layersSelected, onChangedLayers, dataLayers],
    );

    const addLayerToCardHandler = useCallback(
      (e: React.MouseEvent, layer: Layer) =>
      {
        if (!addLayerToCard) return;

        const LayersAdd = [...dataLayers];

        if (waitTimeNotification !== 0)
        {
          clearTimeout(waitTimeNotification);
        }

        waitTimeNotification = window.setTimeout(() =>
        {
          waitTimeNotification = 0;

          const functions: LayerFunctions = {
            onUpdateLayerToCard: updateLayerToCard,
          };

          const result = LayersAdd.find(o => o.LayerId === layer.LayerId);
          const Index = LayersAdd.findIndex(o => o.LayerId === layer.LayerId);

          if (
            result &&
            (result.layerInConversation === false || result.layerInConversation === undefined)
          )
          {
            if (result.layerInConversation !== undefined && Index !== -1 && LayersAdd[Index])
            {
              // Optimistically mark as part of conversation so it becomes selectable.
              // disabled is derived from layerInConversation when restrictToConversationLayers = true.
              LayersAdd[Index].layerInConversation = true;
              LayersAdd[Index].disabled = false;
              setDataLayers(LayersAdd);
            }

            // Force close dropdown
            if (selectRef.current && typeof selectRef.current.blur === 'function')
            {
              selectRef.current.blur();
            }

            if (isConversationDetailsView)
            {
              addLayerToCard(e, layer, functions);
            }
            else
            {
              addLayerToCard(e, layer, functions, conversation.ConversationId);
            }
          }
        }, 500);
      },
      [
        addLayerToCard,
        dataLayers,
        updateLayerToCard,
        isConversationDetailsView,
        conversation.ConversationId,
      ],
    );

    const addLayerToProjectHandler = useCallback(
      (e: React.MouseEvent, layer: Layer) =>
      {
        e.stopPropagation();
        setProjectConfirmLayer(layer);
        setProjectConfirmEvent(e);
      },
      [],
    );

    // Custom Option component for react-select v5
    const CustomOption = useCallback(
      (props: OptionProps<Layer, true>) =>
      {
        const option = props.data;

        let content: React.ReactNode;

        if (
          option.layerInConversation ||
          (!option.ProjectConversationId && !option.configValueId) ||
          (selectableConfigLayers && option.isConfigLayer) ||
          (hideAddLayerPrompt && option.isConfigLayer)
        )
        {
          // Layer already in the conversation, no project context, or EditCard mode where config
          // layers are directly selectable — show just the name
          content = <span className="name">{option.Name || option.LayerName}</span>;
        }
        else if (option.isConfigLayer && canEdit)
        {
          // From GetLayersByConfigValueId, not yet on this card — offer to add it
          content = (
            <div>
              <span className="name">{option.Name || option.LayerName}</span>
              <div style={{ fontSize: 10 }} className="add-new-layer">
                <span style={{ fontStyle: 'italic' }}>
                  (
                  <span style={{ marginRight: 4 }}>
                    This card does not have {option.Name || option.LayerName} layer. Do you want to
                    add it?
                  </span>
                  <b
                    className="add-layer-text"
                    onClick={e =>
                    {
                      e.stopPropagation();
                      addLayerToCardHandler(e, option);
                    }}
                    style={{ color: '#62accb', cursor: 'pointer', fontSize: '9px' }}
                  >
                    Add?
                  </b>
                  )
                </span>
              </div>
            </div>
          );
        }
        else if (option.isConfigLayer && !canEdit && !hideAddLayerPrompt)
        {
          // From GetLayersByConfigValueId but user has no edit rights
          content = (
            <div>
              <span className="name">{option.Name || option.LayerName}</span>
              <div style={{ fontSize: 10 }} className="add-new-layer">
                <span style={{ fontStyle: 'italic' }}>
                  (
                  <span style={{ marginRight: 4 }}>
                    You can not add the {option.Name || option.LayerName} layer to card because you
                    did not create the card. Ask Card Creator or an Admin to add that layer.
                  </span>
                  )
                </span>
              </div>
            </div>
          );
        }
        else
        {
          // From GetLayersForCardBy but NOT in GetLayersByConfigValueId — requires adding to project
          content = (
            <div>
              <span className="name">{option.Name || option.LayerName}</span>
              <div style={{ fontSize: 10 }} className="add-new-layer">
                <span style={{ fontStyle: 'italic' }}>
                  (<span style={{ marginRight: 4 }}>Not available on this card</span>
                  <b
                    className="add-layer-text"
                    onClick={e =>
                    {
                      addLayerToProjectHandler(e, option);
                    }}
                    style={{ color: '#62accb', cursor: 'pointer', fontSize: '9px' }}
                  >
                    Add?
                  </b>
                  )
                </span>
              </div>
            </div>
          );
        }

        return (
          <components.Option {...props}>
            <div
              style={{
                cursor: option.isConfigLayer && !option.layerInConversation && !canEdit
                  ? 'default'
                  : 'pointer',
              }}
            >
              {content}
            </div>
          </components.Option>
        );
      },
      [canEdit, addLayerToCardHandler, addLayerToProjectHandler, selectableConfigLayers, hideAddLayerPrompt],
    );

    // Compute the selected value objects for react-select v5 (needs full objects, not just IDs)
    const selectedValue = useMemo(() =>
    {
      return layersSelected;
    }, [layersSelected]);

    // Hide the chip remove button on the last layer: an existing card must keep at least one
    const isLastLayerLocked = keepOneLayer && layersSelected.length <= 1;
    const selectComponents = useMemo(
      () => (isLastLayerLocked
        ? { Option: CustomOption, MultiValueRemove: LockedMultiValueRemove }
        : { Option: CustomOption }),
      [isLastLayerLocked, CustomOption],
    );

    // Effects
    useEffect(() =>
    {
      if (conversation?.ConversationLayers && !isNewRepost)
      {
        // For existing cards set the initial layer immediately from conversation data.
        // For new replies (isNew) we wait for both API results before picking the default
        // layer, to avoid a visual correction flash if the stored personal layer is not a
        // config layer for this card.
        if (!isNew)
        {
          setInitialLayer();
        }
        setFunctions();
      }

      // Use preloaded layers from parent (e.g. AddInlineTask) to skip the API fetch entirely.
      // configData.preloadedLayers is populated from the GetEmptyCustomFieldsByConfigValueId
      // response which already includes all layer metadata.
      const preloadedLayers = configData?.preloadedLayers as any[] | undefined;
      if (isNew && !isNewRepost && preloadedLayers && preloadedLayers.length > 0)
      {
        hasPickedDefaultLayer.current = true;
        setDataLayers(preloadedLayers as any);

        const candidateLayers = (restrictToConversationLayers
          ? preloadedLayers.filter((l: any) => l.layerInConversation)
          : preloadedLayers.filter((l: any) => l.isConfigLayer));

        const storedLayerId = parseInt(localStorage.getItem('C2_layerid') ?? '0', 10);
        const fromStored =
          storedLayerId > 0
            ? candidateLayers.find((l: any) => l.LayerId === storedLayerId)
            : undefined;
        const defaultLayer = fromStored ?? candidateLayers[0] ?? preloadedLayers[0];

        if (defaultLayer)
        {
          setLayersSelected([defaultLayer]);
          setSelectedIds([defaultLayer.LayerId]);
          setSelectedNames(getLayersName([defaultLayer]));
          setOriginalSelectedIds([defaultLayer.LayerId]);
          setOriginalSelectedLayers([defaultLayer]);
          onChangedLayers?.({ ...conversation, ConversationLayers: [defaultLayer] });
        }
      }
      else
      {
        fetchLayers().then(finalDataLayers =>
        {
          if (!isNew || isNewRepost || !finalDataLayers || hasPickedDefaultLayer.current) return;
          hasPickedDefaultLayer.current = true;

          if (preserveInitialLayers)
          {
            // Duplicate scenario: keep the layers already on the conversation as the initial
            // selection instead of replacing them with the personal default from config.
            const existingLayers = (conversation.ConversationLayers || []).filter(
              l => l.LayerId !== 0,
            );
            if (existingLayers.length > 0)
            {
              setLayersSelected(existingLayers);
              setSelectedIds(existingLayers.map(l => l.LayerId));
              setSelectedNames(getLayersName(existingLayers));
              setOriginalSelectedIds(existingLayers.map(l => l.LayerId));
              setOriginalSelectedLayers(existingLayers);
              onChangedLayers?.({ ...conversation, ConversationLayers: existingLayers });
              return;
            }
          }

          const isProjectCard = conversation?.ConfigValueId === 9;
          const candidateLayers = isProjectCard
            ? finalDataLayers
            : restrictToConversationLayers
              ? finalDataLayers.filter(l => l.layerInConversation)
              : finalDataLayers.filter(l => l.isConfigLayer);

          // Priority:
          // 1. The stored personal layer (C2_layerid) if it is among the candidate layers.
          // 2. First candidate layer from the service.
          const storedLayerId = parseInt(localStorage.getItem('C2_layerid') ?? '0', 10);
          const fromStored =
            storedLayerId > 0 ? candidateLayers.find(l => l.LayerId === storedLayerId) : undefined;
          const defaultLayer = fromStored ?? candidateLayers[0] ?? finalDataLayers[0];

          if (!defaultLayer) return;

          setLayersSelected([defaultLayer]);
          setSelectedIds([defaultLayer.LayerId]);
          setSelectedNames(getLayersName([defaultLayer]));
          setOriginalSelectedIds([defaultLayer.LayerId]);
          setOriginalSelectedLayers([defaultLayer]);
          onChangedLayers?.({ ...conversation, ConversationLayers: [defaultLayer] });
        });
      }

      if (callbackDisableEditor)
      {
        callbackDisableEditor(onDisableEditor);
      }

      if (containerRef.current)
      {
        const containerElement = containerRef.current;
        if (containerElement.closest('.details-tab-directive'))
        {
          setIsFromInfoWindow(true);
        }
        if (containerElement.closest('#canvasDetails'))
        {
          setIsFromTabDetails(true);
        }
      }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const prevConversationRef = useRef<Conversation | null>(null);
    const appliedRepostRef = useRef<string | null>(null);

    useEffect(() =>
    {
      // Repost ids arrive without the conversation changing, so apply them before the skip guard
      if (isNewRepost)
      {
        const repostKey = `${[...layersSelectedIds].sort().join(',')}|${dataLayers.length}`;
        if (appliedRepostRef.current !== repostKey)
        {
          appliedRepostRef.current = repostKey;
          setLayersRepost(layersSelectedIds);
        }
      }
      else
      {
        appliedRepostRef.current = null;
      }

      const prev = prevConversationRef.current;
      const prevLayerIds =
        prev?.ConversationLayers?.map(l => l.LayerId)
          .sort()
          .join(',') ?? '';
      const currLayerIds =
        conversation?.ConversationLayers?.map(l => l.LayerId)
          .sort()
          .join(',') ?? '';
      const layersChanged = prevLayerIds !== currLayerIds;
      const addLayerChanged = prev?.isAddLayerToCard !== conversation?.isAddLayerToCard;
      const configChanged = prev?.selectedConfigValueId !== conversation?.selectedConfigValueId;
      const projectChanged = prev?.ProjectId !== conversation?.ProjectId;

      // Skip if nothing meaningful changed
      if (prev && !layersChanged && !addLayerChanged && !configChanged && !projectChanged)
      {
        prevConversationRef.current = conversation;
        return;
      }

      prevConversationRef.current = conversation;

      // If layers changed or project changed (which implies layers might change), update state.
      // Skip for new replies: the default layer is determined after fetchLayers resolves
      // (both GetLayersByConfigValueId + GetLayersForCardBy), so resolveDefaultLayers would
      // pick from incomplete data and produce a visible invalid selection before correction.
      if (
        !isNew &&
        (layersChanged || projectChanged || (conversation?.isAddLayerToCard && addLayerChanged))
      )
      {
        const allLayers = (conversation.ConversationLayers || []).filter(x => x.LayerId !== 0);
        const layersToUse = resolveDefaultLayers(allLayers);
        const selectedValues = layersToUse.map(item => item.LayerId);
        const selectedNamesNew = getLayersName(layersToUse);
        setLayersSelected(layersToUse);
        setSelectedIds(selectedValues);
        setSelectedNames(selectedNamesNew);
        setOriginalSelectedIds(selectedValues);
        setOriginalSelectedLayers(layersToUse);
        onChangedLayers?.({ ...conversation, ConversationLayers: layersToUse });
      }

      const newConfigValueId = conversation?.selectedConfigValueId ?? conversation?.ConfigValueId;
      if (newConfigValueId && newConfigValueId !== configValueId)
      {
        setConfigValueId(newConfigValueId);
      }

      const newProjectId = conversation?.ProjectId ?? conversation?.ProjectID;
      if (newProjectId && newProjectId !== projectId)
      {
        setProjectId(newProjectId);
      }
    }, [conversation, isNewRepost, layersSelectedIds, resolveDefaultLayers, setLayersRepost]);

    const isDark = document.documentElement.classList.contains('dark');
    const selectStyles: StylesConfig<Layer, true> = {
      control: base => ({
        ...base,
        borderRadius: '0',
        borderColor: 'transparent',
        minHeight: '25px',
        height: 'auto',
        boxShadow: 'none',
        '&:hover': { borderColor: 'transparent' },
        backgroundColor: isDark ? '#2d2f2f' : '#fff',
        border: `1px solid ${isDark ? '#4a4c4c' : '#ccc'}`,
      }),
      valueContainer: base => ({
        ...base,
        padding: '0 6px',
        flexWrap: 'wrap' as const,
        overflow: 'visible',
      }),
      placeholder: base => ({
        ...base,
        lineHeight: '23px',
        paddingLeft: '6px',
        paddingRight: '6px',
        fontSize: '11px',
        color: isDark ? '#6e6860' : base.color,
      }),
      input: base => ({
        ...base,
        minHeight: '23px',
        marginLeft: '6px',
        padding: '2px 0',
        lineHeight: '17px',
        color: isDark ? '#a9a093' : base.color,
      }),
      multiValueLabel: base => ({
        ...base,
        backgroundColor: 'transparent',
        borderBottomRightRadius: '2px',
        borderTopRightRadius: '2px',
        color: isDark ? '#e5e0d8' : '#414042',
        cursor: 'default',
        padding: '2px 5px',
        // react-select base sets paddingLeft 6; match it on the right when the remove button is hidden
        ...(isLastLayerLocked && { paddingRight: '6px' }),
        fontSize: '11px',
        lineHeight: '1.4',
      }),
      multiValueRemove: base => ({
        ...base,
        borderRight: 0,
        borderBottomLeftRadius: '2px',
        borderTopLeftRadius: '2px',
        cursor: 'pointer',
        color: isDark ? '#6e6860' : '#404142',
        padding: '4px 6px 4px 4px',
        ':hover': { backgroundColor: '#62accb', color: '#fff' },
      }),
      multiValue: base => ({
        ...base,
        backgroundColor: isDark ? '#3a3c3c' : 'transparent',
        color: isDark ? '#e5e0d8' : '#414042',
        fontSize: '11px',
        margin: '1px',
        cursor: 'pointer',
        border: `1px solid ${isDark ? '#4a4c4c' : '#ccc'}`,
        borderRadius: '2px',
      }),
      dropdownIndicator: () => ({ display: 'none' }),
      indicatorSeparator: () => ({ display: 'none' }),
      menu: base => ({
        ...base,
        zIndex: 9999999,
        backgroundColor: isDark ? '#333535' : '#fff',
        border: isDark ? '1px solid #4a4c4c' : base.border,
        boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 1px 3px rgba(0,0,0,0.2)',
        maxHeight: '200px',
        borderBottomLeftRadius: '5px',
        borderBottomRightRadius: '5px',
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        marginTop: 0,
        width: '100%',
        minWidth: '200px',
      }),
      menuList: base => ({
        ...base,
        maxHeight: '200px',
        overflowY: 'auto',
        backgroundColor: isDark ? '#333535' : base.backgroundColor,
      }),
      menuPortal: base => ({ ...base, zIndex: 9999999 }),
      option: (base, state) => ({
        ...base,
        padding: '4px 8px',
        fontSize: '10px',
        backgroundColor: state.isFocused ? '#62accb' : isDark ? '#333535' : 'transparent',
        color: state.isFocused ? '#fff' : isDark ? '#a9a093' : base.color,
        border: state.isFocused ? '1px solid #eee' : 'none',
        cursor: 'pointer',
        ':active': { backgroundColor: '#62accb', color: '#fff' },
      }),
    };

    const styleRender: React.CSSProperties = {
      display: 'block',
      zIndex: 9999999,
      top: posTop,
      left: posLeft,
      position: CSSPosition,
      fontSize: isFromInfoWindow || isFromTabDetails ? 15 : undefined,
      width: 'auto',
      minWidth: '150px',
      maxWidth: '200px',
    };

    if (isInlineWidget)
    {
      return (
        <div id={`field-container-${elementId}`}>
          <Select<Layer, true>
            ref={selectRef}
            value={selectedValue}
            options={dataLayers}
            isMulti={true}
            openMenuOnFocus={false}
            closeMenuOnSelect={closeMenuOnSelect}
            isClearable={false}
            autoFocus={false}
            getOptionLabel={option => option.Name || option.LayerName || ''}
            getOptionValue={option => String(option.LayerId)}
            isOptionDisabled={option => !!option.disabled}
            onChange={onSelectLayer}
            placeholder="Select a Layer..."
            components={{ Option: CustomOption }}
            styles={customStyles || selectStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            menuPlacement="top"
          />
          {projectConfirmLayer && createPortal(
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 4, padding: '24px', maxWidth: 400, width: '90%', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>Add layer on card</h3>
                <p style={{ margin: '0 0 8px', fontSize: 14 }}>
                  You must have <strong>{projectConfirmLayer.Name || projectConfirmLayer.LayerName}</strong> layer on both this card and it&apos;s project card to be able to post in that layer.
                </p>
                <p style={{ margin: '0 0 20px', fontSize: 14 }}>Do you want to add the layer to the project as well as this card?</p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => { setProjectConfirmLayer(null); setProjectConfirmEvent(null); }} style={{ padding: '6px 16px', cursor: 'pointer' }}>Cancel</button>
                  <button
                    onClick={() =>
                    {
                      if (addLayerToProject && projectConfirmEvent && projectConfirmLayer)
                      {
                        addLayerToProject(projectConfirmEvent, projectConfirmLayer, { onUpdateLayerToCard: updateLayerToCard });
                      }
                      setProjectConfirmLayer(null);
                      setProjectConfirmEvent(null);
                    }}
                    style={{ padding: '6px 16px', background: '#62accb', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )}
        </div>
      );
    }

    return (
      <div id={`field-container-${elementId}`} className="click-to-edit" ref={containerRef}>
        {!canEdit && <div className="lbl-non-editable-input">{selectedNames || 'Not Set'}</div>}
        {canEdit &&
          (editorEnabled ? (
            <div id={`select-target-layer-${elementId}`}>
              <RenderInBody>
                <div
                  id={`select-render-layer-${elementId}`}
                  className="corgee_com_card-layers-container-inner ConversationOwner-hidden-control-container"
                  style={styleRender}
                >
                  <ClickOutSide onClickOutSide={onSaveByBlur}>
                    <Select<Layer, true>
                      ref={selectRef}
                      value={selectedValue}
                      options={dataLayers}
                      isMulti={true}
                      openMenuOnFocus={true}
                      closeMenuOnSelect={false}
                      isClearable={false}
                      autoFocus={true}
                      getOptionLabel={option => option.Name || option.LayerName || ''}
                      getOptionValue={option => String(option.LayerId)}
                      isOptionDisabled={option => !!option.disabled}
                      onChange={onSelectLayer}
                      placeholder="Select a Layer..."
                      components={selectComponents}
                      styles={selectStyles}
                      menuPortalTarget={null} // We are handling portal manually with RenderInBody
                      menuPosition="absolute" // Inside the RenderInBody container
                      menuIsOpen={true} // Always open when editor is enabled
                      onBlur={handleSelectBlur}
                    />
                  </ClickOutSide>
                </div>
              </RenderInBody>
            </div>
          ) : (
            <div className="lbl-editable-input" onClick={handleEnableEditor}>
              {selectedNames || 'Not Set'}
            </div>
          ))}
        {projectConfirmLayer && createPortal(
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: 4, padding: '24px', maxWidth: 400, width: '90%', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>Add layer on card</h3>
              <p style={{ margin: '0 0 8px', fontSize: 14 }}>
                You must have <strong>{projectConfirmLayer.Name || projectConfirmLayer.LayerName}</strong> layer on both this card and it&apos;s project card to be able to post in that layer.
              </p>
              <p style={{ margin: '0 0 20px', fontSize: 14 }}>Do you want to add the layer to the project as well as this card?</p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => { setProjectConfirmLayer(null); setProjectConfirmEvent(null); }} style={{ padding: '6px 16px', cursor: 'pointer' }}>Cancel</button>
                <button
                  onClick={() =>
                  {
                    if (addLayerToProject && projectConfirmEvent && projectConfirmLayer)
                    {
                      addLayerToProject(projectConfirmEvent, projectConfirmLayer, { onUpdateLayerToCard: updateLayerToCard });
                    }
                    setProjectConfirmLayer(null);
                    setProjectConfirmEvent(null);
                  }}
                  style={{ padding: '6px 16px', background: '#62accb', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      </div>
    );
  },
);

CardConversationLayers.displayName = 'CardConversationLayers';

export default CardConversationLayers;
